import { useCallback, useEffect, useState } from 'react';
import {
  opsApi,
  type OpsDepartmentDto, type OpsStaffMemberDto, type OpsStaffStatus, type OpsStaffSummaryDto,
} from '../api';
import { OpsPanelState, OpsTable, RowActions, opsErrorText, useOpsPanel } from './ops/panel';

/**
 * TICK-121 — departments and crew: the first Operations records to get a screen.
 *
 * Three server rules this screen exists to make visible rather than to work around:
 *
 * - **The list masks contacts.** `emailMasked` / `phoneMasked` are all the crew list ever receives.
 *   Seeing a real phone number takes a deliberate click that fetches the single record, so a screen
 *   left open on a production desk is not a leak, and the audit trail records that someone asked.
 * - **A write carries the version it read.** When someone else saved first the server answers 409
 *   with the record that won; this shows their values and advances the local version, so the next
 *   click is a deliberate overwrite rather than a second collision.
 * - **A department with people in it will not delete.** The server refuses with a count; the screen
 *   repeats that count instead of offering a force.
 *
 * Job title is descriptive only. Nothing here grants access to anything — that is `access_tokens`,
 * a different model on purpose (TICK-111 D3), and the copy says so where an operator will read it.
 */

const STATUS_LABEL: Record<OpsStaffStatus, string> = {
  active: 'ทำงานอยู่',
  standby: 'สแตนด์บาย',
  off_duty: 'ยังไม่เข้างาน',
};

const STATUS_ORDER: OpsStaffStatus[] = ['active', 'standby', 'off_duty'];

export function OpsPeoplePanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [departments, setDepartments] = useState<OpsDepartmentDto[] | null>(null);
  const [crew, setCrew] = useState<OpsStaffSummaryDto[] | null>(null);
  const [loadError, setLoadError] = useState('');

  const [newDepartment, setNewDepartment] = useState('');
  const [editingDepartment, setEditingDepartment] = useState<{ id: string; name: string; version: number } | null>(null);

  const [editing, setEditing] = useState<OpsStaffMemberDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [membership, setMembership] = useState<OpsStaffMemberDto | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        opsApi.listDepartments(token, eventSlug),
        opsApi.listStaff(token, eventSlug),
      ]);
      setDepartments(d);
      setCrew(s);
    } catch (e) {
      setLoadError(opsErrorText(e, 'โหลดข้อมูลทีมงานไม่สำเร็จ'));
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const { busy, error, setError, run } = useOpsPanel(load);

  if (!departments || !crew) return <OpsPanelState error={loadError} />;

  const departmentName = (id: string) => departments.find((d) => d.id === id)?.name ?? id;

  return (
    <div className="ops-panel">
      {error && <div className="form-error">{error}</div>}

      <section>
        <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>ฝ่าย</h3>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
          กลุ่มงานของอีเวนต์นี้ ใช้จัดคนและงานเข้าหมวด — ไม่ใช่สิทธิ์เข้าระบบ
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {departments.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 8 }}>
              {editingDepartment?.id === d.id ? (
                <>
                  <input
                    aria-label={`ชื่อฝ่าย ${d.name}`}
                    value={editingDepartment.name}
                    onChange={(e) => setEditingDepartment({ ...editingDepartment, name: e.target.value })}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-primary"
                    disabled={!!busy || !editingDepartment.name.trim()}
                    onClick={async () => {
                      const ok = await run(() => opsApi.updateDepartment(
                        token, d.id, { name: editingDepartment.name.trim(), version: editingDepartment.version }, eventSlug,
                      ));
                      if (ok) setEditingDepartment(null);
                    }}
                  >
                    บันทึก
                  </button>
                  <button className="btn-link" onClick={() => setEditingDepartment(null)}>ยกเลิก</button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1 }}>{d.name}</span>
                  <button className="btn-link" onClick={() => setEditingDepartment({ id: d.id, name: d.name, version: d.version })}>
                    เปลี่ยนชื่อ
                  </button>
                  <button
                    className="btn-link"
                    disabled={!!busy}
                    onClick={() => {
                      if (!confirm(`ลบฝ่าย "${d.name}"?`)) return;
                      void run(() => opsApi.deleteDepartment(token, d.id, d.version, eventSlug));
                    }}
                  >
                    ลบ
                  </button>
                </>
              )}
            </div>
          ))}
          {departments.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีฝ่าย — เพิ่มฝ่ายแรกด้านล่าง</div>
          )}
        </div>

        <div className="admin-actions" style={{ marginTop: 10 }}>
          <input
            aria-label="ชื่อฝ่ายใหม่"
            placeholder="เช่น Stage, Front of House"
            value={newDepartment}
            onChange={(e) => setNewDepartment(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <button
            className="btn-primary"
            disabled={!!busy || !newDepartment.trim()}
            onClick={async () => {
              const ok = await run(() => opsApi.createDepartment(token, { name: newDepartment.trim() }, eventSlug));
              if (ok) setNewDepartment('');
            }}
          >
            เพิ่มฝ่าย
          </button>
        </div>
      </section>

      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>ทีมงาน</h3>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
              รายชื่อแสดงเบอร์และอีเมลแบบปิดบังไว้ กดดูรายคนจึงเห็นค่าจริง · ตำแหน่งงานที่นี่ไม่ใช่สิทธิ์เข้าระบบ
            </p>
          </div>
          <button className="btn-primary" disabled={!!busy} onClick={() => setCreating(true)}>เพิ่มคน</button>
        </div>

        {crew.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีใครในทีมงานของอีเวนต์นี้</div>
        ) : (
          <OpsTable head={<><th>ชื่อ</th><th>ตำแหน่ง</th><th>ฝ่าย</th><th>สถานะ</th><th>ติดต่อ</th><th /></>}>
                {crew.map((m) => (
                  <tr key={m.id}>
                    <td>{m.displayName}</td>
                    <td style={{ color: 'var(--dim)' }}>{m.jobTitle || '—'}</td>
                    <td>
                      {m.departments.length === 0
                        ? <span style={{ color: 'var(--dim)' }}>—</span>
                        : m.departments.map((d) => (
                          <span key={d.departmentId} style={{ marginRight: 6, fontSize: 11.5 }}>
                            {departmentName(d.departmentId)}{d.isLead ? ' · หัวหน้า' : ''}
                          </span>
                        ))}
                    </td>
                    <td>{STATUS_LABEL[m.status]}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--dim)' }}>
                      {m.phoneMasked ?? m.emailMasked ?? '—'}
                    </td>
                    <td>
                      <RowActions>
                      <button
                        className="btn-link"
                        disabled={!!busy}
                        onClick={async () => {
                          setError('');
                          try {
                            setEditing(await opsApi.getStaffMember(token, m.id, eventSlug));
                          } catch (e) {
                            setError(opsErrorText(e, 'เปิดข้อมูลคนนี้ไม่สำเร็จ'));
                          }
                        }}
                      >
                        แก้ไข
                      </button>
                      <button
                        className="btn-link"
                        disabled={!!busy}
                        onClick={async () => {
                          setError('');
                          try {
                            setMembership(await opsApi.getStaffMember(token, m.id, eventSlug));
                          } catch (e) {
                            setError(opsErrorText(e, 'เปิดข้อมูลคนนี้ไม่สำเร็จ'));
                          }
                        }}
                      >
                        ฝ่าย
                      </button>
                      <button
                        className="btn-link"
                        disabled={!!busy}
                        onClick={() => {
                          if (!confirm(`ลบ "${m.displayName}" ออกจากทีมงาน?`)) return;
                          void run(() => opsApi.deleteStaffMember(token, m.id, m.version, eventSlug));
                        }}
                      >
                        ลบ
                      </button>
                      </RowActions>
                    </td>
                  </tr>
                ))}
          </OpsTable>
        )}
      </section>

      {(creating || editing) && (
        <StaffEditor
          member={editing}
          busy={!!busy}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSave={async (input) => {
            const ok = await run(() => (editing
              ? opsApi.updateStaffMember(token, editing.id, { ...input, version: editing.version }, eventSlug)
              : opsApi.createStaffMember(token, input, eventSlug)));
            if (ok) { setCreating(false); setEditing(null); }
          }}
        />
      )}

      {membership && (
        <MembershipEditor
          member={membership}
          departments={departments}
          busy={!!busy}
          onCancel={() => setMembership(null)}
          onSave={async (rows) => {
            const ok = await run(() => opsApi.setStaffDepartments(token, membership.id, rows, eventSlug));
            if (ok) setMembership(null);
          }}
        />
      )}
    </div>
  );
}

function StaffEditor({ member, busy, onCancel, onSave }: {
  member: OpsStaffMemberDto | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (input: { displayName: string; jobTitle: string; email: string | null; phone: string | null; status: OpsStaffStatus; notes: string }) => void;
}) {
  const [displayName, setDisplayName] = useState(member?.displayName ?? '');
  const [jobTitle, setJobTitle] = useState(member?.jobTitle ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [phone, setPhone] = useState(member?.phone ?? '');
  const [status, setStatus] = useState<OpsStaffStatus>(member?.status ?? 'active');
  const [notes, setNotes] = useState(member?.notes ?? '');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{member ? `แก้ไข ${member.displayName}` : 'เพิ่มคนเข้าทีมงาน'}</h4>

      <div className="ticket-edit-grid">
        <label>
          ชื่อที่ใช้เรียก
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          ตำแหน่งงาน
          <input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="เช่น Sound Engineer FOH" />
        </label>
        <label>
          อีเมล
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="เว้นว่างเพื่อลบออก" />
        </label>
        <label>
          เบอร์โทร
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="เว้นว่างเพื่อลบออก" />
        </label>
        <label>
          สถานะ
          <select value={status} onChange={(e) => setStatus(e.target.value as OpsStaffStatus)}>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </label>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 12 }}>
        บันทึกภายใน
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 8, lineHeight: 1.7 }}>
        ตำแหน่งงานเป็นคำอธิบายเท่านั้น ไม่ได้ให้สิทธิ์อะไรในระบบ — สิทธิ์อยู่ที่ Token ในหน้า “ผู้ใช้ &amp; Token”
      </div>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !displayName.trim()}
          onClick={() => onSave({
            displayName: displayName.trim(),
            jobTitle: jobTitle.trim(),
            // An empty box means "remove this", which `null` says and an empty string does not.
            email: email.trim() === '' ? null : email.trim(),
            phone: phone.trim() === '' ? null : phone.trim(),
            status,
            notes: notes.trim(),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function MembershipEditor({ member, departments, busy, onCancel, onSave }: {
  member: OpsStaffMemberDto;
  departments: OpsDepartmentDto[];
  busy: boolean;
  onCancel: () => void;
  onSave: (rows: { departmentId: string; isLead?: boolean }[]) => void;
}) {
  const [rows, setRows] = useState<Record<string, { member: boolean; isLead: boolean }>>(() => {
    const initial: Record<string, { member: boolean; isLead: boolean }> = {};
    for (const d of departments) initial[d.id] = { member: false, isLead: false };
    for (const d of member.departments) initial[d.departmentId] = { member: true, isLead: d.isLead };
    return initial;
  });

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 13.5 }}>ฝ่ายของ {member.displayName}</h4>
      <p style={{ margin: '0 0 12px', fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.7 }}>
        อยู่ได้หลายฝ่าย · “หัวหน้า” เป็นคำอธิบายหน้าที่ ไม่ใช่สิทธิ์
      </p>

      {departments.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีฝ่ายให้เลือก</div>}

      {departments.map((d) => (
        <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '5px 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, flex: 1 }}>
            <input
              type="checkbox"
              checked={rows[d.id]?.member ?? false}
              onChange={(e) => setRows({ ...rows, [d.id]: { member: e.target.checked, isLead: e.target.checked && (rows[d.id]?.isLead ?? false) } })}
            />
            {d.name}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--dim)' }}>
            <input
              type="checkbox"
              disabled={!(rows[d.id]?.member ?? false)}
              checked={rows[d.id]?.isLead ?? false}
              onChange={(e) => setRows({ ...rows, [d.id]: { member: true, isLead: e.target.checked } })}
            />
            หัวหน้าฝ่าย
          </label>
        </div>
      ))}

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy}
          onClick={() => onSave(
            departments
              .filter((d) => rows[d.id]?.member)
              .map((d) => ({ departmentId: d.id, isLead: rows[d.id]?.isLead ?? false })),
          )}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}
